import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import {
  parseSurveyTeamScheduledSlots,
  surveyTeamRowScheduledOnDate,
  type SurveyTeamScheduledSlot,
} from '../../api/strapiCrm';
import type { MonthAnchor } from '../../sampling/surveyScheduleCalendar';

export type SurveyScheduleCalendarMode = 'view' | 'edit';

export type SurveyCalendarUserRow = {
  numericId: number;
  /** `StrapiManagedUserRow.id` — kunci slot draft. */
  userId: string;
  label: string;
  email: string;
};

export function dynamicRelation(raw: DynamicEntityRow, field: string): Record<string, unknown> | null {
  const value = raw[field];
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function primaryScheduleFromWorkOrder(workOrder: Record<string, unknown>): Record<string, unknown> | null {
  const many = workOrder.sampling_schedules;
  if (Array.isArray(many) && many.length > 0) {
    const rows = many.filter((r): r is Record<string, unknown> => !!r && typeof r === 'object');
    const initial = rows.find((r) => String(r.schedule_kind ?? 'initial') === 'initial');
    return initial ?? rows[0] ?? null;
  }
  return dynamicRelation(workOrder as DynamicEntityRow, 'sampling_schedule');
}

export function scheduleDocIdFromTeamRow(team: DynamicEntityRow): string {
  const fromSchedule = dynamicRelation(team, 'sampling_schedule');
  if (fromSchedule) return String(fromSchedule.documentId ?? '').trim();
  const workOrder = dynamicRelation(team, 'work_order');
  if (!workOrder) return '';
  const schedule = primaryScheduleFromWorkOrder(workOrder);
  return String(schedule?.documentId ?? '').trim();
}

function teamUserNumericId(team: DynamicEntityRow): number | null {
  const userRec = dynamicRelation(team, 'user_idx');
  const uid = Number(userRec?.id);
  return Number.isFinite(uid) ? uid : null;
}

/**
 * Petugas sampling: hanya tanggal di mana petugas yang login punya slot.
 * Anggota tim lain pada tanggal yang sama tetap ikut, supaya kartu tim lengkap.
 * Tanggal jadwal yang sama yang tidak menugaskan petugas ini tidak ditampilkan.
 */
export function surveyTeamsForPetugasCalendarView(
  allTeams: DynamicEntityRow[],
  userNumericId: number,
): DynamicEntityRow[] {
  const datesBySchedule = new Map<string, Set<string>>();
  const datesByWorkOrder = new Map<string, Set<string>>();

  for (const team of allTeams) {
    if (teamUserNumericId(team) !== userNumericId) continue;
    const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);
    if (!slots.length) continue;
    const scheduleId = scheduleDocIdFromTeamRow(team);
    const workOrderId = workOrderDocIdFromTeamRow(team);
    for (const slot of slots) {
      if (scheduleId) {
        const dates = datesBySchedule.get(scheduleId) ?? new Set<string>();
        dates.add(slot.date);
        datesBySchedule.set(scheduleId, dates);
      } else if (workOrderId) {
        const dates = datesByWorkOrder.get(workOrderId) ?? new Set<string>();
        dates.add(slot.date);
        datesByWorkOrder.set(workOrderId, dates);
      }
    }
  }

  if (datesBySchedule.size === 0 && datesByWorkOrder.size === 0) return [];

  const narrowed: DynamicEntityRow[] = [];
  for (const team of allTeams) {
    const scheduleId = scheduleDocIdFromTeamRow(team);
    const workOrderId = workOrderDocIdFromTeamRow(team);
    const allowed = scheduleId
      ? datesBySchedule.get(scheduleId)
      : workOrderId
        ? datesByWorkOrder.get(workOrderId)
        : undefined;
    if (!allowed || allowed.size === 0) continue;

    const slots = parseSurveyTeamScheduledSlots(team.scheduled_date);
    const kept = slots.filter((slot) => allowed.has(slot.date));
    if (!kept.length) continue;
    if (kept.length === slots.length) {
      narrowed.push(team);
      continue;
    }
    narrowed.push({
      ...team,
      scheduled_date: kept.map((slot) => slot.raw),
    });
  }
  return narrowed;
}

export function workOrderDocIdFromTeamRow(team: DynamicEntityRow): string {
  const schedule = dynamicRelation(team, 'sampling_schedule');
  const fromSchedule = schedule
    ? String(dynamicRelation(schedule as DynamicEntityRow, 'work_order')?.documentId ?? '').trim()
    : '';
  if (fromSchedule) return fromSchedule;
  return String(dynamicRelation(team, 'work_order')?.documentId ?? '').trim();
}

/** Map schedule documentId → work order documentId (lintas baris tim survey). */
export function buildWorkOrderDocIdBySchedule(teams: DynamicEntityRow[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of teams) {
    const scheduleDocId = scheduleDocIdFromTeamRow(row);
    const workOrderDocId = workOrderDocIdFromTeamRow(row);
    if (scheduleDocId && workOrderDocId && !map.has(scheduleDocId)) {
      map.set(scheduleDocId, workOrderDocId);
    }
  }
  return map;
}

export function userLabelFromTeamRow(team: DynamicEntityRow): string {
  const userRec = dynamicRelation(team, 'user_idx');
  if (userRec) {
    const username = String(userRec.username ?? '').trim();
    const email = String(userRec.email ?? '').trim();
    return username || email || `User #${userRec.id}`;
  }
  const freelanceRec = dynamicRelation(team, 'freelance_user');
  if (freelanceRec) {
    const name = String(freelanceRec.name ?? '').trim();
    return name || `Freelance #${freelanceRec.documentId ?? freelanceRec.id ?? ''}`;
  }
  return 'Tim survey';
}

/** Stable member key for calendar/draft maps: user id or `freelance:{documentId}`. */
export function memberKeyFromTeamRow(team: DynamicEntityRow): string | null {
  const userRec = dynamicRelation(team, 'user_idx');
  if (userRec) {
    const numericId = Number(userRec.id);
    if (Number.isFinite(numericId)) return String(numericId);
    const docId = String(userRec.documentId ?? '').trim();
    return docId || null;
  }
  const freelanceRec = dynamicRelation(team, 'freelance_user');
  const freelanceDocId = String(freelanceRec?.documentId ?? '').trim();
  return freelanceDocId ? `freelance:${freelanceDocId}` : null;
}

export function userNumericIdFromTeamRow(team: DynamicEntityRow): number | null {
  const rec = dynamicRelation(team, 'user_idx');
  const n = Number(rec?.id);
  return Number.isFinite(n) ? n : null;
}

export function assignmentsForUserDate(
  teams: DynamicEntityRow[],
  userNumericId: number,
  date: string,
): DynamicEntityRow[] {
  return teams.filter((row) => {
    const user = dynamicRelation(row, 'user_idx');
    return Number(user?.id) === userNumericId && surveyTeamRowScheduledOnDate(row, date);
  });
}

export function assignmentsForDate(teams: DynamicEntityRow[], date: string): DynamicEntityRow[] {
  return teams.filter((row) => surveyTeamRowScheduledOnDate(row, date));
}

export function scheduleLinkFromTeamRow(team: DynamicEntityRow): {
  label: string;
  to: string | null;
} {
  const wo = dynamicRelation(team, 'work_order');
  const schedule = dynamicRelation(team, 'sampling_schedule');
  const jobNumber = String(wo?.job_number ?? '').trim();
  const woDocId = String(wo?.documentId ?? '');
  const scheduleDocId = String(schedule?.documentId ?? '');
  const scheduleNo = String(schedule?.schedule_number ?? '').trim();
  const qRaw = schedule ? dynamicRelation(schedule as DynamicEntityRow, 'quotation') : null;
  const qNo = String(qRaw?.quotation_number ?? '').trim();
  const label = jobNumber || scheduleNo || qNo || 'Penjadwalan';
  const to = woDocId
    ? `/sampling/work-order/${encodeURIComponent(woDocId)}/view`
    : scheduleDocId
      ? `/sampling/schedules/${encodeURIComponent(scheduleDocId)}`
      : null;
  return { label, to };
}

export function slotsForUserOnDate(
  slotsByUserId: Map<string, string[]>,
  userId: string,
  date: string,
): SurveyTeamScheduledSlot[] {
  const rawList = slotsByUserId.get(userId) ?? [];
  return parseSurveyTeamScheduledSlots(rawList).filter((s) => s.date === date);
}

export type ScheduleCalendarMeta = {
  scheduleNumber?: string | null;
  quotationDocumentId?: string | null;
  quotationNumber?: string | null;
  customerName?: string | null;
  customerAddress?: string | null;
  scheduleNotes?: string | null;
  scheduleTransportasiNote?: string | null;
};

/** Enrich baris tim survey agar kalender bulanan bisa menampilkan kartu quotation. */
export function enrichSurveyTeamRowsForScheduleCalendar(
  teamRows: DynamicEntityRow[],
  scheduleDocumentId: string,
  scheduleMeta?: ScheduleCalendarMeta,
): DynamicEntityRow[] {
  const addressParts = (scheduleMeta?.customerAddress ?? '').split(',').map((s) => s.trim());
  const samplingSchedule = {
    documentId: scheduleDocumentId,
    schedule_number: scheduleMeta?.scheduleNumber ?? '',
    notes: scheduleMeta?.scheduleNotes ?? '',
    transportasi_note: scheduleMeta?.scheduleTransportasiNote ?? '',
    quotation: {
      documentId: scheduleMeta?.quotationDocumentId ?? '',
      quotation_number: scheduleMeta?.quotationNumber ?? '',
      customer: {
        name: scheduleMeta?.customerName ?? '',
        address: addressParts[0] ?? '',
        city: addressParts[1] ?? '',
        province: addressParts[2] ?? '',
      },
    },
  };
  return teamRows.map((row) => ({
    ...row,
    sampling_schedule: samplingSchedule,
  })) as DynamicEntityRow[];
}

export function surveyTeamScheduleSummaryLabel(raw: unknown): string {
  const slots = parseSurveyTeamScheduledSlots(raw);
  if (slots.length === 0) return '—';
  return slots
    .map((slot) => (slot.timeLabel ? `${slot.date} ${slot.timeLabel}` : slot.date))
    .join(', ');
}

export function firstSurveyTeamScheduleDate(raw: unknown): string | null {
  const slots = parseSurveyTeamScheduledSlots(raw);
  return slots[0]?.date ?? null;
}

function slotTimeForDisplay(timeLabel: string): string {
  return /^\d{2}:\d{2}$/.test(timeLabel.trim()) ? timeLabel.trim() : '08:00';
}

function groupSurveySlotsIntoDisplayRanges(
  slots: SurveyTeamScheduledSlot[],
): Array<{ startDate: string; finishDate: string; time: string }> {
  if (slots.length === 0) return [];
  const sorted = [...slots].sort((a, b) => a.date.localeCompare(b.date));
  const ranges: Array<{ startDate: string; finishDate: string; time: string }> = [];
  let startDate = sorted[0].date;
  let finishDate = sorted[0].date;
  let time = slotTimeForDisplay(sorted[0].timeLabel);

  for (let i = 1; i < sorted.length; i += 1) {
    const slot = sorted[i];
    const slotTime = slotTimeForDisplay(slot.timeLabel);
    const prev = new Date(`${finishDate}T00:00:00`);
    const next = new Date(`${slot.date}T00:00:00`);
    const dayDiff = (next.getTime() - prev.getTime()) / 86_400_000;
    if (slotTime === time && dayDiff === 1) {
      finishDate = slot.date;
      continue;
    }
    ranges.push({ startDate, finishDate, time });
    startDate = slot.date;
    finishDate = slot.date;
    time = slotTime;
  }

  ranges.push({ startDate, finishDate, time });
  return ranges;
}

export type SurveyTeamDisplayEntry = {
  id: string;
  userName: string;
  startDate: string;
  finishDate: string;
  time: string;
  isLead: boolean;
};

export function buildSurveyTeamDisplayEntries(teamRows: DynamicEntityRow[]): SurveyTeamDisplayEntry[] {
  const sorted = [...teamRows].sort((a, b) => {
    if (Boolean(a.is_lead) !== Boolean(b.is_lead)) return a.is_lead ? -1 : 1;
    return userLabelFromTeamRow(a).localeCompare(userLabelFromTeamRow(b), 'id');
  });

  const entries: SurveyTeamDisplayEntry[] = [];
  for (const row of sorted) {
    const ranges = groupSurveySlotsIntoDisplayRanges(parseSurveyTeamScheduledSlots(row.scheduled_date));
    const rowId = String(row.documentId ?? userLabelFromTeamRow(row));
    ranges.forEach((range, index) => {
      entries.push({
        id: `${rowId}-${index}`,
        userName: userLabelFromTeamRow(row),
        startDate: range.startDate,
        finishDate: range.finishDate,
        time: range.time,
        isLead: Boolean(row.is_lead) && index === 0,
      });
    });
    if (ranges.length === 0) {
      entries.push({
        id: `${rowId}-empty`,
        userName: userLabelFromTeamRow(row),
        startDate: '',
        finishDate: '',
        time: '',
        isLead: Boolean(row.is_lead),
      });
    }
  }
  return entries;
}

export type { MonthAnchor, SurveyTeamScheduledSlot };
